import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_DEBOUNCE_MS = 500;
const MIN_DEBOUNCE_MS = 300;
const MAX_DEBOUNCE_MS = 800;

const canUseLocalStorage = () => {
  if (typeof window === "undefined") return false;

  try {
    if (!window.localStorage) return false;
    const testKey = "__local_storage_test__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (_error) {
    return false;
  }
};

const safeParseDraft = (rawDraft) => {
  if (!rawDraft) return null;

  try {
    const parsed = JSON.parse(rawDraft);
    if (!parsed || typeof parsed !== "object") return null;

    // Current draft shape stores metadata next to the quotation state.
    if (Object.prototype.hasOwnProperty.call(parsed, "state")) {
      return {
        state: parsed.state,
        savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : null,
      };
    }

    // Backward-compatible fallback if an older draft only stored the state object.
    return {
      state: parsed,
      savedAt: null,
    };
  } catch (_error) {
    return null;
  }
};

const normalizeDebounceMs = (value) => {
  const delay = Number(value);
  if (!Number.isFinite(delay)) return DEFAULT_DEBOUNCE_MS;
  return Math.min(MAX_DEBOUNCE_MS, Math.max(MIN_DEBOUNCE_MS, delay));
};

/**
 * Persist complex form state to localStorage and safely restore it once.
 *
 * localStorage only stores strings, so the hook serializes a small envelope:
 * { state: <quotation state>, savedAt: <ISO timestamp> }.
 *
 * The hook is SSR-safe, handles private/incognito storage failures, debounces
 * writes, and exposes metadata for UI feedback.
 *
 * IMPORTANT: Restore happens synchronously during the first render so that
 * any initialisation useEffect in the consumer component sees the restored
 * state and does not overwrite it with defaults.
 */
export default function useAutoSaveDraft(key, state, setState, options = {}) {
  const { enabled = true, debounceMs = DEFAULT_DEBOUNCE_MS } = options;
  const initialStateJsonRef = useRef(null);
  const lastSavedJsonRef = useRef(null);
  const hydratedRef = useRef(false);
  const skipWritesUntilRef = useRef(0);
  const [isDraftRestored, setIsDraftRestored] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const safeDebounceMs = useMemo(() => normalizeDebounceMs(debounceMs), [debounceMs]);

  /**
   * Keep a ref pointing at the most recent state object so the
   * beforeunload handler can synchronously write it to localStorage
   * without waiting for a debounced timeout.
   */
  const latestStateRef = useRef(state);
  latestStateRef.current = state;

  if (initialStateJsonRef.current === null) {
    try {
      initialStateJsonRef.current = JSON.stringify(state);
    } catch (_error) {
      initialStateJsonRef.current = "";
    }
  }

  // ── Synchronous draft restore (render-phase) ──────────────────────
  // Must run before any useEffect so the consumer component's
  // initialisation effect sees the restored state, not the defaults.
  if (enabled && key && canUseLocalStorage() && !hydratedRef.current) {
    try {
      const rawDraft = window.localStorage.getItem(key);
      const draft = safeParseDraft(rawDraft);
      if (draft && draft.state && typeof draft.state === "object") {
        // Calling setState during render is valid in React 18; React will
        // schedule a re-render with the restored values *before* flushing
        // any effects, which is exactly what we need.
        setState(draft.state);

        if (draft.savedAt) {
          setLastSavedAt(draft.savedAt);
        }
        setIsDraftRestored(true);

        try {
          lastSavedJsonRef.current = JSON.stringify(draft.state);
        } catch (_error) {
          lastSavedJsonRef.current = null;
        }
      } else if (rawDraft) {
        // Invalid JSON should not crash the app or keep breaking future loads.
        window.localStorage.removeItem(key);
      }
    } catch (_error) {
      // localStorage can become unavailable between capability checks.
    }

    hydratedRef.current = true;
  }

  /**
   * Synchronously persist the latest state when the page is about to
   * unload (browser refresh, tab close, navigation away) so the
   * debounce gap doesn't cause data loss.
   */
  useEffect(() => {
    if (!enabled || !key || !canUseLocalStorage()) return undefined;

    const handleBeforeUnload = () => {
      if (Date.now() < skipWritesUntilRef.current) return;

      try {
        const savedAt = new Date().toISOString();
        const payload = JSON.stringify({ state: latestStateRef.current, savedAt });
        window.localStorage.setItem(key, payload);
        lastSavedJsonRef.current = JSON.stringify(latestStateRef.current);
        setLastSavedAt(savedAt);
      } catch (_error) {
        // Storage failure must never block page navigation.
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [enabled, key]);

  useEffect(() => {
    if (!enabled || !key || !hydratedRef.current || !canUseLocalStorage()) return undefined;

    let stateJson;
    try {
      stateJson = JSON.stringify(state);
    } catch (_error) {
      return undefined;
    }

    if (stateJson === lastSavedJsonRef.current) return undefined;

    const timeoutId = window.setTimeout(() => {
      if (Date.now() < skipWritesUntilRef.current) return;

      try {
        const savedAt = new Date().toISOString();
        const payload = JSON.stringify({ state, savedAt });
        window.localStorage.setItem(key, payload);
        lastSavedJsonRef.current = stateJson;
        setLastSavedAt(savedAt);
      } catch (_error) {
        // Quota/security failures should never interrupt quotation editing.
      }
    }, safeDebounceMs);

    return () => window.clearTimeout(timeoutId);
  }, [enabled, key, safeDebounceMs, state]);

  const clearDraft = useCallback(() => {
    if (!key || !canUseLocalStorage()) return;

    try {
      window.localStorage.removeItem(key);
      lastSavedJsonRef.current = null;
      setLastSavedAt(null);
      setIsDraftRestored(false);
      // Successful quotation saves usually reset multiple fields immediately.
      // Suppress transient reset writes so localStorage stays clear after save.
      skipWritesUntilRef.current = Date.now() + safeDebounceMs + 250;
    } catch (_error) {
      // Storage can fail even after initial checks; never crash the form.
    }
  }, [key, safeDebounceMs]);

  return { isDraftRestored, lastSavedAt, clearDraft };
}